import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthService } from '../../../api/services/auth/auth.service';
import {
  PATH_CHANGE_PASSWORD,
  PATH_DASHBOARD,
  PATH_EMPRESAS,
  PATH_LANDING,
  PATH_LOGIN,
} from '../../../routes/routes.constants';

/**
 * Resuelve si hay una sesión usable. Con el accessToken vencido pero el usuario guardado se
 * intenta renovar en silencio con la cookie refresh (httpOnly) antes de mandar al login.
 */
function ensureSession(authService: AuthService): Observable<boolean> {
  if (!authService.loggedIn()) return of(false);
  if (!authService.isTokenExpired()) return of(true);

  return authService.refreshAccessToken().pipe(
    map(() => true),
    catchError(() => of(false)),
  );
}

/**
 * Rutas del panel. Rol único (Admin/SuperAdmin): con sesión válida tiene acceso total.
 * Si la contraseña es temporal, solo se permite la pantalla de cambio de contraseña.
 */
export const AuthGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return ensureSession(authService).pipe(
    map((hasSession): boolean | UrlTree => {
      if (hasSession) {
        return authService.mustChangePassword()
          ? router.createUrlTree([`/${PATH_CHANGE_PASSWORD}`])
          : true;
      }

      authService.clearLocalSession();

      // Entrada por la raíz → landing pública. Deep link → login, volviendo después a la ruta pedida.
      if (state.url === '/' || state.url === `/${PATH_DASHBOARD}`) {
        return router.createUrlTree([`/${PATH_LANDING}`]);
      }
      return router.createUrlTree([`/${PATH_LOGIN}`], {
        queryParams: { returnUrl: state.url },
      });
    }),
  );
};

/** Pantalla de cambio de contraseña: requiere sesión, aunque la contraseña sea temporal. */
export const changePasswordGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return ensureSession(authService).pipe(
    map((hasSession) =>
      hasSession ? true : router.createUrlTree([`/${PATH_LOGIN}`]),
    ),
  );
};

/** Cuentas: SuperAdmin y Admin de la empresa. */
export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.canManageUsers() ? true : router.createUrlTree([homeFor(authService)]);
};

/** Empresas: solo el SuperAdmin. */
export const superAdminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isSuperAdmin() ? true : router.createUrlTree([homeFor(authService)]);
};

/** Editor de videos: solo el Admin (ediciones de su empresa). Las plantillas son videos, no se editan. */
export const videoEditorGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.canEditVideos() ? true : router.createUrlTree([homeFor(authService)]);
};

/** Módulos de una empresa: el SuperAdmin no opera dentro de las empresas → vuelve a Empresas. */
export const companyGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isSuperAdmin() ? router.createUrlTree([`/${PATH_EMPRESAS}`]) : true;
};

/** Pantalla de inicio según el rol. */
export function homeFor(authService: AuthService): string {
  return authService.isSuperAdmin() ? `/${PATH_EMPRESAS}` : `/${PATH_DASHBOARD}`;
}

/** Pantalla de login: con sesión válida no tiene sentido mostrarla. */
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.loggedIn() && !authService.isTokenExpired()) {
    return router.createUrlTree([
      `/${authService.mustChangePassword() ? PATH_CHANGE_PASSWORD : PATH_DASHBOARD}`,
    ]);
  }
  return true;
};
